/**
 * Page of subscription selection.
 *
 * Copyright © Ircam Amplify 2025
 */
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useRef, useState } from 'react'
import { API, fetchCompanyConsumption, fetchUser } from '../api'
import Button from '../components/Button'
import FullScreenLayout from '../components/FullScreenLayout'

import { EXTERNAL_URLS } from '@/constant/externalUrl'

// constant declarations
const TypeMin = 'min'
const TypeApiCall = 'API call'
const ModulePrice = [
  { name: 'Audio-Text Aligner', price: '0.05 credits', type: TypeMin },
  { name: 'Time-Pitch Control', price: '0.05 credits', type: TypeMin },
  { name: 'Stereo To Spatial', price: '1 credit', type: TypeMin },
  { name: 'Loop Extractor', price: '0.05 credits', type: TypeMin },
  { name: 'AI Re-editor', price: '0.05 credits', type: TypeMin },
  //{ name: 'Speech To Text',        price: '0.05 credits', type: TypeMin }, // Only for an enterprise client
  { name: 'Music Tagger', price: '0.05 credits', type: TypeApiCall },
  { name: 'Cover Detector', price: '0.05 credits', type: TypeApiCall },
  { name: 'Infringement Detector', price: '0.05 credits', type: TypeApiCall },
  { name: 'Quality Check', price: '0.05 credits', type: TypeApiCall },
  { name: 'AI Music Detector', price: '0.1 credits', type: TypeApiCall },
  { name: 'AI Speech Detector', price: '0.1 credits', type: TypeApiCall },
  { name: 'Talk Analyzer', price: '0.05 credits', type: TypeApiCall },
  { name: 'Stem Separator', price: '0.05 credits', type: TypeMin },
]

export interface SubscriptionData {
  eur_standard: Price[]
  eur_out_package: Price[]
}

export interface Price {
  id: string
  object: 'price'
  active: boolean
  billing_scheme: string
  created: number
  currency: string
  custom_unit_amount: number | null
  livemode: boolean
  lookup_key: string
  metadata: Metadata
  nickname: string
  product: string
  recurring: Recurring
  tax_behavior: string
  tiers_mode: unknown | null
  transform_quantity: unknown | null
  type: string
  unit_amount: number | null
  unit_amount_decimal: string
}

export interface Metadata {
  credits: string
  overdue_credits_limit: string
}

export interface Recurring {
  aggregate_usage: string | null
  interval: string
  interval_count: number
  meter: null
  trial_period_days: null
  usage_type: string
}

export const SelectTier = () => {
  const [prices, setPrices] = useState<SubscriptionData | null>(null)
  const [isEuroCurrency] = useState(true)
  // By default, annual subscription is selected (isMonthly false)
  const [isMonthly, setIsMonthly] = useState(false)

  const secondSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchUser()
    fetchCompanyConsumption()
    const getPrices = async () => {
      const { data } = await API(await fetchAuthSession()).get(
        '/checkout/prices'
      )
      if (data && data.prices) {
        setPrices(data.prices)
      }
    }
    getPrices()
  }, [])

  /**
   * Returns a Price based on the plan key and frequency.
   */
  const getBasePlanPrice = (planKey: string) => {
    if (!prices) return null
    const price = prices.eur_standard.find(
      (p) =>
        p.lookup_key.includes(planKey) &&
        p.lookup_key.includes(isMonthly ? 'monthly' : 'yearly')
    )
    return price || null
  }

  const getOverPlanPrice = (planKey: string) => {
    if (!prices) return null
    const price = prices.eur_out_package.find(
      (p) =>
        p.lookup_key.includes(planKey) &&
        p.lookup_key.includes(isMonthly ? 'monthly' : 'yearly')
    )
    return price || null
  }

  /**
   * Format the price for display on the card.
   * For monthly subscriptions, show the unit amount per month.
   * For annual subscriptions, calculate and show the monthly cost (annual total divided by 12).
   */
  const formatMonthlyPrice = (priceObj: Price | null) => {
    if (!priceObj || !priceObj.unit_amount) return '—'
    const symbol = isEuroCurrency ? '€' : '$'
    if (isMonthly) {
      const amount = (priceObj.unit_amount / 100).toFixed(0)
      return `${symbol}${amount}/month`
    } else {
      // Annual: display monthly cost (annual total divided by 12)
      const monthlyAmount = (priceObj.unit_amount / 100 / 12).toFixed(0)
      return `${symbol}${monthlyAmount}/month`
    }
  }

  /**
   * Format the annual total price for annual subscriptions.
   */
  const formatAnnualPrice = (priceObj: Price | null) => {
    if (!priceObj || !priceObj.unit_amount) return ''
    const symbol = isEuroCurrency ? '€' : '$'
    // This value represents the total annual cost
    const annualAmount = (priceObj.unit_amount / 100).toFixed(0)
    return `${symbol}${annualAmount}`
  }

  // Retrieve the Price objects for Starter and Pro plans.
  const starterPrice = getBasePlanPrice('starter')
  const proPrice = getBasePlanPrice('pro')

  const starterOverPrice = getOverPlanPrice('starter')
  const proOverPrice = getOverPlanPrice('pro')

  // Toggle frequency between monthly and annual
  const handleFrequencyChange = (value: boolean) => {
    setIsMonthly(value)
  }
  const starterOverCreditCost = starterOverPrice?.unit_amount_decimal
    ? Number(starterOverPrice?.unit_amount_decimal) * 10
    : null
  const proOverCreditCost = proOverPrice?.unit_amount_decimal
    ? Number(proOverPrice?.unit_amount_decimal) * 10
    : null

  const handleContactClick = () => {
    window.location.href = EXTERNAL_URLS.CONTACT
  }

  const ContactButton = () => {
    return (
      <Button
        label="Contact us"
        className="w-full bg-red-500 text-white py-2 rounded-md cursor-pointer"
        onClick={handleContactClick}
      />
    )
  }

  return (
    <FullScreenLayout
      className="!h-auto min-h-screen bg-white text-black"
      title="Simple, transparent pricing"
      subtitle={
        <p>
          Choose the plan that fits your{' '}
          <span
            className="text-[color:var(--accentColor)] cursor-pointer hover:underline"
            onClick={() => secondSectionRef.current?.scrollIntoView()}
          >
            usage
          </span>
        </p>
      }
    >
      {/* Toggle for monthly vs. annual (with annual as default) */}
      <div className="flex justify-center items-center mb-8">
        <div className="inline-flex items-center relative border border-gray-300 rounded-full">
          <button
            type="button"
            className={`
              px-4 py-2 rounded-full
              ${isMonthly ? 'bg-gray-500 text-black' : 'text-gray-300'}
            `}
            onClick={() => handleFrequencyChange(true)}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`
              px-4 py-2 rounded-full
              ${!isMonthly ? 'bg-gray-500 text-black' : 'text-gray-300'}
            `}
            onClick={() => handleFrequencyChange(false)}
          >
            Annually
          </button>
          <div
            className={`
              absolute px-2 py-1 text-xs
              rounded-full top-[-12px] right-[-30px]
              ${isMonthly ? 'bg-gray-500 text-black' : 'bg-[color:var(--accentColor)] text-white'}
            `}
          >
            -20%
          </div>
        </div>
      </div>

      {/* Pricing cards in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Starter plan */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-between shadow-lg border border-gray-300">
          <div>
            <h3 className="text-xl font-semibold mb-2 text-black">Starter</h3>
            <p className="text-md mb-4 text-gray-600">
              Ideal for personal or small projects.
            </p>
            <p className="text-3xl font-bold text-black">
              {formatMonthlyPrice(starterPrice)}
            </p>
            <p className="text-sm text-black mt-1">
              {isMonthly
                ? 'Billed monthly'
                : `Billed annually  (Total: ${formatAnnualPrice(starterPrice)})`}
            </p>
            <ul className="mt-4 text-md space-y-2 text-gray-600 list-disc pl-5">
              <li>
                Up to {isMonthly ? 130 : 1600} tracks per{' '}
                {isMonthly ? 'month' : 'year'}*
              </li>
              <li>Access all modules</li>
              <li>API access</li>
              <li>Cancel anytime</li>
              <li>
                Off-Package (Additional) Credits:{' '}
                {starterOverCreditCost ? `${starterOverCreditCost}€` : '-'} per
                credit
              </li>
            </ul>
          </div>
          <div className="mt-6">
            <ContactButton />
          </div>
        </div>

        {/* Pro plan */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-between shadow-lg border border-[color:var(--accentColor)] relative">
          <div className="absolute top-0 right-0 bg-orange-600 text-xs px-2 py-1 rounded-bl-lg text-[color:var(--accentColor)]">
            BEST VALUE
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2 text-black">Pro</h3>
            <p className="text-md mb-4 text-gray-600">
              Perfect for professional users with higher volume.
            </p>
            <p className="text-3xl font-bold text-black">
              {formatMonthlyPrice(proPrice)}
            </p>
            <p className="text-sm text-black mt-1">
              {isMonthly
                ? 'Billed monthly'
                : `Billed annually  (Total: ${formatAnnualPrice(proPrice)})`}
            </p>
            <ul className="mt-4 text-md space-y-2 text-gray-600 list-disc pl-5">
              <li>
                Up to {isMonthly ? 400 : 4800} tracks per{' '}
                {isMonthly ? 'month' : 'year'}*
              </li>
              <li>Access all modules</li>
              <li>API access</li>
              <li>Cancel anytime</li>
              <li>
                Off-Package (Additional) Credits:{' '}
                {proOverCreditCost ? `${proOverCreditCost}€` : '-'} per credit
              </li>
            </ul>
          </div>
          <div className="mt-6">
            <ContactButton />
          </div>
        </div>

        {/* Enterprise plan remains unchanged */}
        <div className="bg-white rounded-xl p-6 flex flex-col justify-between shadow-lg border border-gray-300">
          <div>
            <h3 className="text-xl font-semibold mb-2 text-black">
              Enterprise
            </h3>
            <p className="text-md mb-4 text-gray-600">
              Tailored for organizations with large scale usage.
            </p>
            <p className="text-3xl font-bold text-black">On demand</p>
            <p className="text-sm text-black mt-1">Custom plan</p>
            <ul className="mt-4 text-md space-y-2 text-gray-600 list-disc pl-5">
              <li>Unlimited tracks</li>
              <li>Dedicated account manager</li>
              <li>Custom integrations</li>
              <li>Premium support</li>
            </ul>
          </div>
          <div className="mt-6">
            {/* <Button
              label="Contact us"
              className="w-full bg-red-500 text-white py-2 rounded-md cursor-pointer"
              onClick={handleContactClick}
            /> */}
            <ContactButton />
          </div>
        </div>
      </div>
      <p className="text-sm text-gray-200 text-center mb-10 mt-6">
        {' '}
        * Estimations based on 3 Minutes tracks for Music Tagger
      </p>

      {/* Optional second section for additional info such as modules or FAQ */}
      <div ref={secondSectionRef} className="mt-10 mb-20">
        <div className="text-center text-4xl font-semibold mt-14">
          Module prices
        </div>
        <div className="text-center mt-4 mb-14">
          <div className="text-xl my-4">
            Estimate how many credits you need for your usage
          </div>
        </div>
        <div className="flex flex-wrap justify-center items-start gap-4">
          <div className="border border-gray-400 rounded-2xl p-4 w-fit min-w-[240px] md:w-[320px] sm:w-[280px] w-full">
            <div className="font-bold text-[22px] mb-8">Module name</div>
            {ModulePrice.map((module) => (
              <div
                key={`${module.name}Name`}
                className="font-medium text-[16px] mb-4"
              >
                {module.name}
              </div>
            ))}
          </div>
          <div className="rounded-2xl p-4 w-fit bg-[#F8F8F8] min-w-[240px] md:w-[320px] sm:w-[280px] w-full self-stretch">
            <div className="font-bold text-[22px] mb-8">Credit consumption</div>
            {ModulePrice.map((module) => (
              <div
                key={`${module.name}Price`}
                className="font-normal text-[16px] mb-4"
              >
                {module.price} / {module.type}
              </div>
            ))}
          </div>
        </div>
      </div>
    </FullScreenLayout>
  )
}
